import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
	ROLES_AND_PERMISSION_ENABLED,
	useAuthContext,
} from '../../../../../context/auth';
import UsersApi, { USERS_KEY } from '../../api/users';
import {
	NEW_USER_DIALOG_TITLE,
	TABLE_NO_USERS_FOUND_TEXT,
	USERS_HEADER,
	USERS_NEW_USER_BUTTON_TEXT,
	USERS_PAGE_SSO_HELPER_TEXT_1,
	USERS_PAGE_SSO_HELPER_TEXT_2,
	USERS_TABLE_ACTIONS_HEADER,
	USERS_TABLE_CREATED_HEADER,
	USERS_TABLE_EMAIL_HEADER,
	USERS_TABLE_NAME_HEADER,
	USERS_TABLE_PERMISSIONS_HEADER,
	USERS_TABLE_ROLES_HEADER,
	USERS_TABLE_STATUS_HEADER,
	USERS_TABLE_USERNAME_HEADER,
	USERS_TITLE_TEXT,
} from '../../data/constants';
import { handleModalClose } from '../../utils/handleModalClose';
import UserForm from './UserForm';
import UserRow from './UserRow';

export default function Users() {
	const authData = useAuthContext();
	const rolesAndPermissionsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			ROLES_AND_PERMISSION_ENABLED
		];
	const { tenantHostname } = useParams();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const [open, setOpen] = React.useState(false);
	const { data, isLoading, error } = UsersApi.useUsers(
		[USERS_KEY, tenantId],
		tenantHostname,
		authToken,
	);

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	return (
		<Stack spacing={2} height='100%'>
			<Stack direction='row' spacing={2}>
				<Typography variant='h3' gutterBottom>
					{USERS_TITLE_TEXT()}
				</Typography>
				{!tenantHostname && (
					<Button
						variant='contained'
						color='secondary'
						startIcon={<AddIcon />}
						onClick={() => handleClickOpen()}
					>
						{USERS_NEW_USER_BUTTON_TEXT()}
					</Button>
				)}
			</Stack>
			{tenantHostname && (
				<Stack
					direction='row'
					spacing={1}
					alignContent='center'
					color='#777777'
				>
					<InfoOutlinedIcon fontSize='small' />
					<Typography variant='body2' align='left'>
						{USERS_PAGE_SSO_HELPER_TEXT_1()}
						<Link to='admin/users'>{USERS_HEADER()}</Link>
						{USERS_PAGE_SSO_HELPER_TEXT_2()}
					</Typography>
				</Stack>
			)}
			<Paper sx={{ flex: 1, overflowY: 'auto' }}>
				<TableContainer sx={{ maxHeight: '100%' }}>
					<Table size='small' stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_USERNAME_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_NAME_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_EMAIL_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{rolesAndPermissionsEnabled
											? USERS_TABLE_ROLES_HEADER()
											: USERS_TABLE_PERMISSIONS_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_STATUS_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_CREATED_HEADER()}
									</Typography>
								</TableCell>
								<TableCell sx={{ backgroundColor: 'white.lightest' }}>
									<Typography fontWeight='bold'>
										{USERS_TABLE_ACTIONS_HEADER()}
									</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Skeleton width='100%' />
									</TableCell>
								</TableRow>
							) : error ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography variant='body1'>
											{TABLE_NO_USERS_FOUND_TEXT()}
										</Typography>
									</TableCell>
								</TableRow>
							) : !data || data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={8}>
										<Typography variant='body1'>
											{TABLE_NO_USERS_FOUND_TEXT()}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								data
									?.sort((a, b) => {
										// sort by username
										if (a.username < b.username) {
											return -1;
										}
										return 1;
									})
									.map((user, index) => (
										<UserRow
											key={index}
											user={user}
											rolesAndPermissionEnabled={rolesAndPermissionsEnabled}
										/>
									))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			<Dialog open={open} onClose={(e, r) => handleModalClose(r, handleClose)}>
				<DialogTitle>{NEW_USER_DIALOG_TITLE()}</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<UserForm
						handleClose={handleClose}
						users={data}
						rolesAndPermissionEnabled={rolesAndPermissionsEnabled}
					/>
				</DialogContent>
			</Dialog>
		</Stack>
	);
}
